import React, { useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { fmtEUR, fmtDate } from "../../lib/format";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { FileDown, CheckCircle2, RotateCcw, Paperclip, Receipt } from "lucide-react";
import { toast } from "sonner";

export const downloadQuietanza = async (r) => {
  try { const res = await api.get(`/ritenute/${r.id}/quietanza`, { responseType: "blob" }); const u = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = u; a.download = `Quietanza_${r.codice}_${r.quietanza_nome || "f24.pdf"}`; a.click(); URL.revokeObjectURL(u); }
  catch { toast.error("Quietanza non disponibile"); }
};

export const QuietanzaUpload = ({ rit, onDone }) => {
  const ref = React.useRef();
  const up = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/ritenute/${rit.id}/quietanza`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(rit.stato === "da_versare" ? "Quietanza allegata e ritenuta segnata versata" : "Quietanza allegata"); onDone(); }
    catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    e.target.value = "";
  };
  return (<>
    <input ref={ref} type="file" accept="application/pdf,image/*" className="hidden" onChange={up} data-testid="ritenuta-quietanza-input" />
    <Button variant="ghost" size="icon" title={rit.quietanza_doc_id ? "Sostituisci quietanza" : "Allega quietanza F24"} onClick={() => ref.current?.click()} data-testid="ritenuta-quietanza-upload-btn"><Paperclip className="w-4 h-4" /></Button>
  </>);
};

export const downloadF24 = async (r) => {
  try { const res = await api.get(`/ritenute/${r.id}/f24`, { responseType: "blob" }); const u = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = u; a.download = `F24_${r.codice}.pdf`; a.click(); URL.revokeObjectURL(u); }
  catch { toast.error("Generazione F24 non riuscita"); }
};

export const VersataDialog = ({ rit, onClose }) => {
  const [data, setData] = useState(new Date().toISOString().slice(0, 10));
  const [prot, setProt] = useState("");
  const save = async () => {
    try { await api.post(`/ritenute/${rit.id}/versata`, { data_versamento: data, protocollo: prot || null }); toast.success("Ritenuta segnata come versata"); onClose(true); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!rit} onOpenChange={() => onClose(false)}>
      <DialogContent data-testid="ritenuta-versata-dialog">
        <DialogHeader><DialogTitle>Segna F24 versato — {rit?.codice}</DialogTitle></DialogHeader>
        <div className="text-sm text-muted-foreground">{rit?.associato} · {fmtEUR(rit?.importo)} · tributo {rit?.codice_tributo} · periodo {rit?.periodo}</div>
        <div className="grid gap-3">
          <div><Label className="text-xs">Data versamento</Label><Input type="date" value={data} onChange={(e) => setData(e.target.value)} data-testid="ritenuta-data-versamento" /></div>
          <div><Label className="text-xs">Protocollo telematico quietanza (facoltativo)</Label><Input value={prot} onChange={(e) => setProt(e.target.value)} placeholder="es. 3466774" data-testid="ritenuta-protocollo" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onClose(false)}>Annulla</Button><Button onClick={save} data-testid="ritenuta-versata-confirm">Conferma versamento</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RitenuteTable = ({ rows, onChanged, isAdmin, showAssociato = true }) => {
  const [sel, setSel] = useState(null);
  const riapri = async (r) => { try { await api.post(`/ritenute/${r.id}/riapri`); toast.success("Ritenuta riaperta"); onChanged(); } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } };
  if (!rows.length) return <div className="text-sm text-muted-foreground" data-testid="ritenute-empty">Nessuna ritenuta registrata.</div>;
  return (
    <>
      <Table>
        <TableHeader><TableRow className="bg-muted/50"><TableHead>Codice</TableHead>{showAssociato && <TableHead>Associato</TableHead>}<TableHead>Contratto / operazione</TableHead><TableHead>Bonifico</TableHead><TableHead>Tributo · periodo</TableHead><TableHead className="text-right">Ritenuta</TableHead><TableHead>Scadenza F24</TableHead><TableHead>Stato</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>{rows.map((r) => (
          <TableRow key={r.id} data-testid="ritenuta-row">
            <TableCell className="font-medium">{r.codice}<div className="text-[10px] text-muted-foreground">{r.societa}</div></TableCell>
            {showAssociato && <TableCell>{r.associato}{r.tipo === "acconto" && <Badge variant="outline" className="ml-1 text-[10px]">fornitore</Badge>}</TableCell>}
            <TableCell className="text-xs">{r.contratto}<div className="text-muted-foreground">{r.operazione}</div>{r.tipo === "altro" && <div className="text-violet-700">{r.descrizione}</div>}</TableCell>
            <TableCell className="text-xs">{fmtDate(r.data_bonifico)}{r.tipo !== "acconto" && r.tipo !== "altro" && <div className="text-muted-foreground">netto {fmtEUR(r.netto_bonificato)}</div>}</TableCell>
            <TableCell className="text-xs">{r.codice_tributo} · {r.periodo}</TableCell>
            <TableCell className="text-right font-semibold">{fmtEUR(r.importo)}</TableCell>
            <TableCell className={r.scaduta ? "text-destructive font-semibold" : ""}>{fmtDate(r.scadenza)}{r.stato === "da_versare" && <div className="text-[10px]">{r.scaduta ? `scaduta da ${-r.giorni} gg` : `tra ${r.giorni} gg`}</div>}</TableCell>
            <TableCell>{r.stato === "versata" ? <Badge className="bg-emerald-600">Versata {fmtDate(r.data_versamento)}</Badge> : <Badge variant={r.scaduta ? "destructive" : "outline"}>Da versare</Badge>}{r.protocollo && <div className="text-[10px] text-muted-foreground">prot. {r.protocollo}</div>}{r.quietanza_doc_id && <div className="text-[10px] text-emerald-700 flex items-center gap-1" data-testid="ritenuta-quietanza-badge"><Receipt className="w-3 h-3" /> quietanza allegata</div>}</TableCell>
            <TableCell className="whitespace-nowrap">
              <Button variant="ghost" size="icon" title="Scarica F24" onClick={() => downloadF24(r)} data-testid="ritenuta-f24-btn"><FileDown className="w-4 h-4" /></Button>
              {r.quietanza_doc_id && <Button variant="ghost" size="icon" title="Scarica quietanza" onClick={() => downloadQuietanza(r)} data-testid="ritenuta-quietanza-btn"><Receipt className="w-4 h-4 text-emerald-600" /></Button>}
              {isAdmin && <QuietanzaUpload rit={r} onDone={onChanged} />}
              {isAdmin && r.stato === "da_versare" && <Button variant="ghost" size="icon" title="Segna versata" onClick={() => setSel(r)} data-testid="ritenuta-segna-versata-btn"><CheckCircle2 className="w-4 h-4 text-emerald-600" /></Button>}
              {isAdmin && r.stato === "versata" && <Button variant="ghost" size="icon" title="Riapri" onClick={() => riapri(r)}><RotateCcw className="w-4 h-4" /></Button>}
            </TableCell>
          </TableRow>))}
        </TableBody>
      </Table>
      <VersataDialog rit={sel} onClose={(ok) => { setSel(null); if (ok) onChanged(); }} />
    </>
  );
};

export default function RitenutePanel({ investorId, isAdmin }) {
  const [d, setD] = useState(null);
  const load = () => api.get(`/ritenute`, { params: { investor_id: investorId } }).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, [investorId]);
  if (!d) return null;
  return (
    <div data-testid="investor-ritenute">
      <div className="text-xs text-muted-foreground mb-2">Da versare {fmtEUR(d.totale_da_versare)} · versato {fmtEUR(d.totale_versato)}</div>
      <RitenuteTable rows={d.items} onChanged={load} isAdmin={isAdmin} showAssociato={false} />
    </div>
  );
}
